import React, { useEffect, useState, useCallback } from "react";
import { Table, Tag, Typography, Select, Space, Button, Card } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { privateAdminGetApi } from "../../utils/apiHelper";

const { Title, Text } = Typography;

const OUTCOME_OPTIONS = [
  { value: "loaded", label: "Loaded" },
  { value: "token_issued", label: "Token issued" },
  { value: "confirmed", label: "Confirmed" },
  { value: "pending_confirm", label: "Pending confirm" },
  { value: "spam", label: "Spam" },
  { value: "rate_limited", label: "Rate limited" },
  { value: "duplicate", label: "Duplicate" },
  { value: "origin_blocked", label: "Origin blocked" },
  { value: "validation_failed", label: "Validation failed" },
  { value: "honeypot", label: "Honeypot" },
  { value: "rejected", label: "Rejected" },
  { value: "form_not_found", label: "Form not found" },
  { value: "server_error", label: "Server error" },
];

const OUTCOME_COLORS = {
  loaded: "cyan",
  token_issued: "geekblue",
  confirmed: "green",
  pending_confirm: "blue",
  spam: "orange",
  rate_limited: "red",
  duplicate: "gold",
  origin_blocked: "red",
  validation_failed: "volcano",
  honeypot: "purple",
  rejected: "red",
  form_not_found: "default",
  server_error: "red",
};

function FormLoadsChart({ organizationId }) {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const query = { days: 30 };
    if (organizationId) query.organizationId = organizationId;
    privateAdminGetApi("admin/form-logs/stats", query)
      .then((res) => {
        setSeries(res.data.result.series);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [organizationId]);

  return (
    <Card
      size="small"
      title="Form loads vs. tokens vs. submissions (last 30 days, all forms)"
      style={{ marginBottom: 16 }}
      loading={loading}
    >
      {series.length ? (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={series}>
            <CartesianGrid stroke="#E8ECEF" strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="loaded"
              name="Loads"
              stroke="#4A5A61"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="tokenIssued"
              name="Tokens issued"
              stroke="#1565C0"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="submitted"
              name="Submissions"
              stroke="#2E7D32"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Text type="secondary">No data in the last 30 days.</Text>
      )}
    </Card>
  );
}

export default function AdminFormLogs() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [organizationId, setOrganizationId] = useState(undefined);
  const [outcome, setOutcome] = useState(undefined);

  useEffect(() => {
    privateAdminGetApi("admin/organizations").then((res) => {
      setOrgs(res.data.result.records);
    });
  }, []);

  const fetchLogs = useCallback(() => {
    setLoading(true);
    const query = {};
    if (organizationId) query.organizationId = organizationId;
    if (outcome) query.outcome = outcome;
    privateAdminGetApi("admin/form-logs", query)
      .then((res) => {
        setRecords(res.data.result.records);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [organizationId, outcome]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const columns = [
    {
      title: "Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => new Date(v).toLocaleString(),
      width: 190,
    },
    {
      title: "Organization",
      dataIndex: "organizationName",
      key: "organizationName",
      render: (v) => v || "-",
    },
    {
      title: "Form",
      dataIndex: "formName",
      key: "formName",
      render: (v, r) => v || r.publicId || "-",
    },
    {
      title: "Outcome",
      dataIndex: "outcome",
      key: "outcome",
      render: (v) => <Tag color={OUTCOME_COLORS[v] || "default"}>{v}</Tag>,
    },
    { title: "Status", dataIndex: "statusCode", key: "statusCode", width: 80 },
    { title: "IP", dataIndex: "ip", key: "ip" },
    {
      title: "Detail",
      dataIndex: "detail",
      key: "detail",
      ellipsis: true,
    },
  ];

  return (
    <div>
      <Space
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Title level={5} style={{ marginBottom: 0 }}>
          Form request logs
        </Title>
        <Space>
          <Select
            allowClear
            placeholder="Organization"
            style={{ width: 220 }}
            value={organizationId}
            onChange={setOrganizationId}
            options={orgs.map((o) => ({ value: o._id, label: o.name }))}
          />
          <Select
            allowClear
            placeholder="Outcome"
            style={{ width: 180 }}
            value={outcome}
            onChange={setOutcome}
            options={OUTCOME_OPTIONS}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchLogs} />
        </Space>
      </Space>
      <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        Every public form request attempt, including rate-limited/blocked/spam
        ones. Retained indefinitely.
      </Text>
      <FormLoadsChart organizationId={organizationId} />
      <Table
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={records}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 900 }}
      />
    </div>
  );
}
